import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';

import { IUserWallet } from '@share/common-types';
import { FunctionalRootState, GetWalletAmount, RootState } from '@share/utils';
import { DateType, getDashboarReports, IReportsResponse } from '@store/slices';

import { isAccountTravelAgent } from './utils';

// `leisureCredits` arrives as a string (possibly formatted, e.g. "69,380"); null when it isn't a number.
export const parsePoints = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

export interface IAccountBalances {
  /** Money wallet (`account.hasClientCash`, or a wallet that can be cashed out, with wallet data loaded). */
  showWallet: boolean;
  wallet: IUserWallet | null | undefined;
  formatWallet: (value: number | null | undefined) => string;
  /** Leisure credits / points, or null when the member has none to show. */
  points: number | null;
  pointsLabel: string;
  /** Travel agents' commissions from the agent report (My reports). */
  showCommissions: boolean;
  commissions: IReportsResponse | null;
  commissionsRange: string;
  formatUsd: (value: number | null | undefined) => string;
}

/**
 * Every balance the hub shows (money wallet, leisure credits / points, agent commissions), shared by
 * the Overview cards and the account menu so the two always agree.
 */
export const useAccountBalances = (): IAccountBalances => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const { account, userWalletData, cashOutAvailable } = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuItems = useSelector((state: FunctionalRootState) => state.navigationMenuStore?.items);
  const profile = useSelector((state: FunctionalRootState) => state.myProfileStore?.result?.profile);
  const dashboardReportsStore = useSelector((state: FunctionalRootState) => state.dashboardReportsStore);

  // Agents' commission wallets have `hasClientCash` off but can be cashed out: that wallet is their money.
  const showWallet = (!!account?.hasClientCash || !!cashOutAvailable) && !!userWalletData;
  const formatWallet = (value: number | null | undefined) =>
    GetWalletAmount(value, account, userWalletData, account?.walletClientCashName);

  // Leisure credits / points: the same menu `leisureCredits` balance the header and checkout show
  // ("Leisure credits balance after reservation"), gated by the same points settings; else the
  // profile's points (what the Modern sidebar shows). Label follows checkout: the promo site's points
  // name when it has one, otherwise "Leisure credits".
  const pointsSettings = menuItems?.pointsSettings;
  const isPointsTenant = !!menuItems?.isPromoSite || [pointsSettings?.hotel, pointsSettings?.condo, pointsSettings?.car]
    .some(setting => !!setting?.isPointsControlEnabled || !!setting?.isRemainingBalanceEnabled);
  const menuCredits = parsePoints(menuItems?.leisureCredits);
  const profilePoints = typeof profile?.points === 'number' && profile.points > 0 ? profile.points : null;
  const points = !account?.isMLM ? ((isPointsTenant ? menuCredits : null) ?? profilePoints) : null;
  const pointsLabel = (menuItems?.isPromoSite && menuItems?.promoSiteName)
    || intl.formatMessage({ id: isPointsTenant && menuCredits !== null ? 'my.account.hub.points.leisure_title' : 'my.account.hub.points.title' });

  // Travel agents: their commissions (My reports' figures) for the report's current period.
  const showCommissions = isAccountTravelAgent(account, menuItems);
  const commissions = dashboardReportsStore?.dashBoardData || null;
  const commissionsRange = intl.formatMessage({
    id: DateType[dashboardReportsStore?.request?.dateType ?? DateType.LAST_30] || 'CUSTOM_RANGE',
  });
  const formatUsd = (value: number | null | undefined) =>
    intl.formatNumber(value || 0, { style: 'currency', currency: 'USD' });

  // The menu loads the report for MLM agents; load it once here if it hasn't been.
  const reportRequested = useRef(false);
  useEffect(() => {
    if (showCommissions && !reportRequested.current && !commissions && !dashboardReportsStore?.loading) {
      reportRequested.current = true;
      dispatch(getDashboarReports());
    }
  }, [dispatch, showCommissions, commissions, dashboardReportsStore?.loading]);

  return {
    showWallet,
    wallet: userWalletData,
    formatWallet,
    points,
    pointsLabel,
    showCommissions,
    commissions,
    commissionsRange,
    formatUsd,
  };
};
